'use client'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useState } from 'react'

// Styles
import styles from './addresses.module.scss'

import useAddress from '@src/quince-site-integrated/src/hooks/useAddress'

// Components
import AccountWrapper from '../../components/Account/AccountWrapper'
import AddressModal from '../../components/Account/AddressModal'
import PageHeader from '../../components/Account/PageHeader'
import Button from '../../components/Button'

import { getAllStatesOption } from '@src/quince-site-common/src/services/addressService/addressService'
import { trackEvent } from '@src/telemetry/track'
import Address from '../../components/Account/Address'

import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { parseLocale } from '@src/utils/i18n.utils'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'

const Addresses = ({ data }) => {
  const { t } = useTranslation('accountPage')
  const { t: tCommon } = useTranslation('common')
  const { locale } = useLocaleContext()
  const { country } = parseLocale(locale)
  const { skipProvinceInAddress } = useStatsigDynamicLocaleConfig()

  const page = data.accountPage

  const [modalOpen, setModalOpen] = useState(false)
  const [stateOptions, setStateOptions] = useState([])

  const { savedAddress, defaultAddress, getRefreshedSavedAddress } = useAddress()

  useEffect(() => {
    if (!skipProvinceInAddress) {
      getAllStatesOption(country.toUpperCase()).then(stateOptions => {
        setStateOptions(stateOptions.states)
      })
    }
    getRefreshedSavedAddress()
  }, [country, getRefreshedSavedAddress, skipProvinceInAddress])

  const sendAddressAddtionSuccessfulEvent = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.ADDRESS_ADDITION_SUCCESSFUL, {
      default_address_present: defaultAddress ? true : false,
      no_of_address: savedAddress?.length,
    })
  }, [defaultAddress, savedAddress])

  const sendAddressEditSuccessfulEvent = useCallback(
    defaultAddress => {
      trackEvent(TELEMETRY_EVENT_NAMES.ADDRESS_EDIT_SUCCESSFUL, {
        is_default_address: defaultAddress,
        no_of_address: savedAddress?.length,
      })
    },
    [savedAddress],
  )

  const getItemType = itemLength =>
    itemLength % 3 === 0 ? styles.tripletItem : itemLength % 3 === 2 ? styles.doubleItem : styles.defaultItem

  const refreshCustomerAddress = () => {
    getRefreshedSavedAddress()
  }

  return (
    <AccountWrapper heroImage={page.hero} menu={page.menuList} contact={page.contact} activeSlug={page.slug}>
      <PageHeader title={page.title} />
      <div className={styles.container}>
        <div className={styles.addresses}>
          <Button
            className={styles.styledButton}
            onClick={() => {
              setModalOpen(true)
              trackEvent(TELEMETRY_EVENT_NAMES.ADDRESS_ADDITION_INITIATED, {
                default_address_present: defaultAddress ? true : false,
                no_of_address: savedAddress?.length,
              })
            }}
          >
            {t('addAddress')}
          </Button>

          <ul className={styles.addressList}>
            {defaultAddress && (
              <li className={styles.addressItem} count={savedAddress.length} isDefault>
                <Address
                  title={t('defaultAdrress')}
                  name={defaultAddress.firstName}
                  firstName={defaultAddress.firstName}
                  lastName={defaultAddress.lastName}
                  address1={defaultAddress.addressLine1}
                  address2={defaultAddress.addressLine2}
                  city={defaultAddress.city}
                  provinceCode={defaultAddress.provinceCode}
                  province={defaultAddress.provinceCode}
                  phone={defaultAddress.phoneNumber}
                  zip={defaultAddress.zip}
                  country={defaultAddress.countryCode}
                  id={defaultAddress.id}
                  refreshCustomerAddress={refreshCustomerAddress}
                  addressForm={page.addAddressForm}
                  customerId={defaultAddress.customerId}
                  stateOptions={stateOptions}
                  isDefault
                  skipProvinceInAddress={skipProvinceInAddress}
                />
              </li>
            )}
            {savedAddress.map((address, idx) => {
              return (
                <li
                  className={`${styles.addressItem} ${getItemType(savedAddress.length)}`}
                  key={address.id}
                  count={savedAddress.length}
                >
                  <Address
                    title={tCommon('var1_var2_v4', { var1: t('address'), var2: idx + 1 })}
                    name={address.firstName}
                    firstName={address.firstName}
                    lastName={address.lastName}
                    address1={address.addressLine1}
                    address2={address.addressLine2}
                    city={address.city}
                    provinceCode={address.provinceCode}
                    province={address.provinceCode}
                    phone={address.phoneNumber}
                    zip={address.zip}
                    country={address.countryCode}
                    id={address.id}
                    refreshCustomerAddress={refreshCustomerAddress}
                    addressForm={page.addAddressForm}
                    customerId={address.customerId}
                    stateOptions={stateOptions}
                    skipProvinceInAddress={skipProvinceInAddress}
                  />
                </li>
              )
            })}
          </ul>
        </div>
      </div>
      <AddressModal
        open={modalOpen}
        closeModal={() => {
          setModalOpen(false)
        }}
        addressForm={page.addAddressForm}
        refreshCustomerAddress={refreshCustomerAddress}
        initialFormData={{}}
        // @i18n-ignore
        mode="add"
        id={null}
        sendAddressAddtionSuccessfulEvent={sendAddressAddtionSuccessfulEvent}
        sendAddressEditSuccessfulEvent={sendAddressEditSuccessfulEvent}
        stateOptions={stateOptions}
        skipProvinceInAddress={skipProvinceInAddress}
      />
    </AccountWrapper>
  )
}

export default Addresses
